import { Avatar, Box, Button, Link, Typography, useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React, { useState, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { API } from 'src/common/config/API';
import {
  selectTrialPlanTrackEvent,
  selectTrialTrackEvent,
  signUpTrackEvent
} from 'src/common/Analytics';
import {
  createAccountLink,
  getClientReferenceId,
  getCookie,
  handleInterComClick,
  localStorageMethod,
  removeSquareSpaceToken,
  setCookie,
  setSquareSpaceToken,
  preventSpaceInput,
  removeAffiliateCoolie
} from 'src/common/config/FunctionHelper';

import {
  accessDeniedErrorSQAP,
  emailRegex,
  paths,
  SQSPDescription,
  squareSpaceUtm,
  isLoginOrSignup,
  signUpTexts,
  signupTexts,
  continueWithSquareSpace,
  localseoSignup,
  localSeoSignupSource,
  localSeoLogo,
  localseoSignupSqspFlow,
  localSeoSignupSourceSqsp,
  SEOSpaceLocal
} from 'src/common/config/TextMessage';
import { AuthSignupFormTypes } from 'src/content/signup/types/SquareSpaceAuthSignupFormType';
import { RootState } from 'src/redux/store';
import {
  hasLocalSeoCookie,
  removeUtmCookie,
  setUtmCookie
} from 'src/common/config/FunctionHelperPartTwo';
import { LocalSeoIconSx, SquareSpaceConnectSx } from 'src/common/CommomSxs';
import { signupSxs } from './signupSx';
import CommonAlert from 'src/common/CommonAlert';
import Intercom from 'react-intercom';
import { INTERCOM_APP_ID } from 'src/appConfig';
import SignUpToolPage from './SignUpToolPage';
import { loginSxs } from 'src/content/login/components/loginSxs';
import { buySearchCreditModalSxs } from 'src/content/keywordResearch/keywordResearchCSS';
import SignupFooter from './SignupFooter';
import { LocalSeoToolPage } from './localSeoSignupComponenets/LocalSeoToolPage';
import { setIsOpenChangePlan } from 'src/redux/siteAuditReducer';
import { setTempId } from 'src/redux/userReducer';
/**
 * SquareSpace authentication component.
 */
// SquareSpace authentication component
const SquareSpaceAuthSignUp = () => {
  const [loading, setLoading] = useState(false);
  const [screenLoading, setScreenLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [showNameFiled, setShowNameFiled] = useState(false);
  const [error, setError] = useState<string>('');
  const [teamSizeValue, setTeamSizeValue] = useState('');
  const [yourRoleValue, setYourRoleValue] = useState('');
  const [errorSQAP, setErrorSQAP] = useState<boolean>(false);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] =
    React.useState(false);
  const [otherFields, setOtherFields] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [isTermError, setIsTermError] = useState(false);
  const [openIntercom, setOpenIntercom] = useState(null);
  const [sqSpAuthLoader, setSqSpAuthLoader] = useState(null);
  const [isNotConnected, setIsNotConnected] = useState(null);
  const [alreadyConnected, setAlreadyConnected] = useState(null);
  const navigationLocation = useLocation();
  const navigator = useNavigate();
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  let stripeSubscriptionDetails = useSelector(
    (state: RootState) => state.user.subscription
  );
  const tempId = localStorageMethod('get', 'temp_id') || null;
  const isFreePlan = activePlanName === planDetails.free;
  const isFromLoginSqsp =
    localStorageMethod('get', 'from_login_sqsp') == 'true';
  // Redux state
  let squareSpaceProcessDetails: any = useSelector(
    (state: RootState) => state.user.squareSpaceProcess
  );
  // get getCookie
  let utm = getCookie(squareSpaceUtm);

  const getLocalSeoCookie = getCookie(localseoSignup) ?? null;
  const getLocalSeoCookieSqsp = getCookie(localseoSignupSqspFlow) ?? null;
  const isLocalSeo = getLocalSeoCookie === localSeoSignupSource;
  const isLocalSeoSqsp = getLocalSeoCookieSqsp === localSeoSignupSourceSqsp;
  const isLocalSeoSession = hasLocalSeoCookie();
  const dispatch = useDispatch();
  const isProfileSqSp = localStorageMethod('get', 'is_profile_sqsp');
  useEffect(() => {
    // Set a timeout to update the state after 3 seconds
    const timeout = setTimeout(() => {
      setOpenIntercom(true);
    }, 1000); // 3 seconds in milliseconds

    // Cleanup the timeout when the component unmounts
    return () => clearTimeout(timeout);
  }, []);

  const theme = useTheme(); // Access the theme
  const styles = buySearchCreditModalSxs(theme);
  // SquareSpace form auth validation
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      email: '',
      name: '',
      teamSize: '',
      yourRole: '',
      otherText: ''
    }
  });
  const yourRole: string = watch('yourRole');

  // SquareSpace signUp form auth validation error message
  let signUpValidationErrorMessage = {
    email: {
      required: 'Email Address is required',
      pattern: {
        value: emailRegex,
        message: 'Invalid email address'
      }
    },
    name: {
      required: 'Name is required',
      pattern: {
        value: /^[a-z /-]+$/i,
        message: 'Only add alphabets'
      }
    },
    teamSize: {
      required: 'Please select a team size'
    },
    yourRole: {
      required: 'Please select a your role'
    }
  };

  useEffect(() => {
    if (squareSpaceProcessDetails?.signUp) {
      setShowNameFiled(true);
      setValue('email', squareSpaceProcessDetails?.email);
      setValue('name', squareSpaceProcessDetails?.name);
      setValue('teamSize', squareSpaceProcessDetails?.teamSize);
      setValue('yourRole', squareSpaceProcessDetails?.yourRole);
      setValue('otherText', squareSpaceProcessDetails?.otherText);
      setTeamSizeValue(squareSpaceProcessDetails?.teamSize);
      setYourRoleValue(squareSpaceProcessDetails?.yourRole);
      setIsChecked(squareSpaceProcessDetails?.isChecked);
      if (squareSpaceProcessDetails?.isChecked == true) {
        localStorageMethod('set', 'has_agree_terms_conditions', 'true');
      }
    } else {
      if (location.pathname === paths.squarespaceAuth) {
        setShowNameFiled(true);
      } else {
        setShowNameFiled(false);
      }
    }
  }, [squareSpaceProcessDetails]);

  // Set Watch confirm password
  useEffect(() => {
    if (yourRole === 'Other') {
      setOtherFields(true);
    } else {
      setOtherFields(false);
    }
  }, [yourRole]);
  // After SquareSpace set value in text box and store token
  useEffect(() => {
    // When page load
    const queryParams = new URLSearchParams(navigationLocation.search);
    // Access specific query parameters
    const codeParam = queryParams.get('code');
    const accessDeniedError = queryParams.get('error');
    const squareSpaceUserEmail = localStorageMethod(
      'get',
      'squareSpaceAuthEmail'
    );
    const squareSpaceAuthName = localStorageMethod(
      'get',
      'squareSpaceAuthName'
    );
    const squareSpaceTeamSize = localStorageMethod(
      'get',
      'squareSpaceTeamSize'
    );
    const squareSpaceYourRole = localStorageMethod(
      'get',
      'squareSpaceYourRole'
    );
    const squareSpaceOther = localStorageMethod('get', 'squareSpaceOther');
    const hasAgreedToTermsLocal = localStorageMethod(
      'get',
      'has_agree_terms_conditions'
    );

    // Check if user in come to signup or not
    if (codeParam && squareSpaceUserEmail && squareSpaceAuthName) {
      setShowNameFiled(true);
      const addOrbitAiAddon = localStorageMethod('get', 'addOrbitAiAddon');
      const orbitAiAddonPriceId = localStorageMethod('get', 'orbitAiAddonPriceId');

      let requestPayLoad: any = {
        email: squareSpaceUserEmail,
        name: squareSpaceAuthName,
        code: codeParam,
        selectPlanId: localStorageMethod('get', 'selectedPlanID'),
        clientReferenceId: getClientReferenceId(),
        ...(addOrbitAiAddon === 'true' && orbitAiAddonPriceId
          ? { orbitAiPriceId: orbitAiAddonPriceId }
          : {}),
        team_size: squareSpaceTeamSize,
        your_role:
          squareSpaceYourRole == 'Other'
            ? squareSpaceOther
            : squareSpaceYourRole,
        utm: utm ? utm : null,
        isLocalSeo: utm ? false : isLocalSeo || isLocalSeoSession
        //hasAgreedToTerms: hasAgreedToTermsLocal == 'true' ? true : false
      };

      if (tempId) {
        requestPayLoad.tempId = tempId;
      }

      // Set email address value
      if (squareSpaceUserEmail) {
        setValue('email', squareSpaceUserEmail);
      }
      // set name value
      if (squareSpaceAuthName) {
        setValue('name', squareSpaceAuthName);
      }
      // set team size
      if (squareSpaceTeamSize) {
        setValue('teamSize', squareSpaceTeamSize);
        setTeamSizeValue(squareSpaceTeamSize);
      }
      // set your role
      if (squareSpaceYourRole) {
        setYourRoleValue(squareSpaceYourRole);
        setValue('yourRole', squareSpaceYourRole);
        setValue('otherText', squareSpaceOther);
      }

      // Start store token value
      setLoading(true);
      API.squareAuthToken(
        API.EndPoint.squareAuthToken,
        API.Method.post,
        undefined,
        requestPayLoad,
        (response) => {
          const responseDetails = { ...response.data };
          removeUtmCookie(isLoginOrSignup);
          if (responseDetails.status === 'success') {
            removeSquareSpaceToken();
            localStorageMethod('remove', 'has_agree_terms_conditions');
            localStorageMethod('remove', 'addOrbitAiAddon');
            localStorageMethod('remove', 'orbitAiAddonPriceId');
            setIsSuccessfullySubmitted(true);
            setSuccessMessage(responseDetails.message);
            if (responseDetails.isSignUp) {
              localStorageMethod('set', 'isSignUp', 'true');
              localStorageMethod('set', 'isSignUpLocalSeo', 'true');
              localStorageMethod('set', 'signUpToken', responseDetails.token);
              setCookie(responseDetails?.token, responseDetails?.user?.name);
              if ((window as any).gr) {
                (window as any).gr('track', 'conversion', {
                  email: responseDetails?.user?.email
                });
                removeAffiliateCoolie();
              }
              if (responseDetails.redirectUrl) {
                let payload = {
                  email: responseDetails?.user?.email,
                  sourceDetail: 'signup page'
                };
                signUpTrackEvent(payload); // SignUp Track Event
                if (
                  responseDetails.redirectUrl == paths.siteAudit ||
                  responseDetails.redirectUrl == paths.externalScans
                ) {
                  if (responseDetails.redirectUrl == paths.siteAudit) {
                    selectTrialPlanTrackEvent(
                      responseDetails?.user?.email,
                      requestPayLoad?.selectPlanId
                    ); // Plan Track Event
                  } else {
                    selectTrialTrackEvent(responseDetails?.user?.email); // Free Plan Track Event
                  }
                  setCookie(
                    responseDetails?.token,
                    responseDetails?.user?.name
                  );
                }
                setTimeout(() => {
                  window.location.href = responseDetails?.redirectUrl;
                }, 1500);
              } else {
                navigator(paths.price);
              }
            } else {
              // Redirect to dashboard
              // After 2 seconds reset error message
              setCookie(responseDetails?.token, responseDetails?.user?.name);

              setTimeout(() => {
                let utm = getCookie(squareSpaceUtm);
                // navigator(utm ? paths?.squareSpaceConnectUtm : paths.siteAudit);
                navigator(paths.dashboard);
              }, 1500);
            }
          } else {
            setError(responseDetails.message);
            setAlreadyConnected(responseDetails?.alreadyConnected);
            // After 2 seconds reset error message
            setTimeout(() => {
              setError('');
              setLoading(false);
              setAlreadyConnected(false);
            }, 5000);
          }
          setLoading(false);
          dispatch(setTempId(null));
          localStorageMethod('remove', 'temp_id');
        }
      );
    }
    // Check if user in come to signin or not
    if (codeParam && !squareSpaceAuthName) {
      setShowNameFiled(false);
      let requestPayLoad = {
        email: squareSpaceUserEmail || '',
        code: codeParam,
        isLocalSeo: utm ? false : isLocalSeo || isLocalSeoSession
      };

      // Start store token value
      setLoading(true);
      setErrorSQAP(false);
      API.squareConnect(
        API.EndPoint.squareConnect,
        API.Method.post,
        undefined,
        requestPayLoad,
        (response) => {
          const responseDetails: any = { ...response.data };
          removeUtmCookie(isLoginOrSignup);
          if (responseDetails.status === 'success') {
            removeSquareSpaceToken();
            if (isLocalSeoSqsp) {
              navigator(paths?.squareSpaceConnectUtmLocalSeo);
            } else if (isLocalSeo && !isProfileSqSp) {
              navigator(paths?.localChecker);
            } else if (isFromLoginSqsp) {
              navigator(
                isFreePlan && !stripeSubscriptionDetails?.isFreeWithAI
                  ? paths?.externalScans
                  : utm
                  ? paths?.squareSpaceConnectUtm
                  : paths?.dashboard
              );
            }
            localStorageMethod('remove', 'is_profile_sqsp');
            setIsSuccessfullySubmitted(true);
            setSuccessMessage(responseDetails.message);

            setCookie(responseDetails?.token, responseDetails?.user?.name);
          } else {
            setError(responseDetails?.message);
            setIsNotConnected(responseDetails?.isNotConnected);
            setAlreadyConnected(responseDetails?.alreadyConnected);
          }
          setLoading(false);
        }
      );
    }
    // when user clicks cancel, then show the error
    if (accessDeniedError) {
      setLoading(true);
      setErrorSQAP(true);
    }

    setTimeout(() => {
      setScreenLoading(false);
    }, 2000);
  }, []);

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
    if (event.target.checked) {
      setIsTermError(false);
      localStorageMethod('set', 'has_agree_terms_conditions', 'true');
    } else {
      localStorageMethod('set', 'has_agree_terms_conditions', 'false');
    }
  };

  // SquareSpace SignUp Form Submit handler
  const onSubmit: SubmitHandler<AuthSignupFormTypes> = async (request) => {
    //if (!isChecked) {
    //  setIsTermError(true);
    //} else {
    setIsTermError(false);
    const { email, name, teamSize, yourRole, otherText } = request;
    setSquareSpaceToken(name, email);
    localStorageMethod('set', 'squareSpaceTeamSize', teamSize);
    localStorageMethod('set', 'squareSpaceYourRole', yourRole);
    localStorageMethod('set', 'squareSpaceOther', otherText);
    setUtmCookie(isLoginOrSignup, 'true');
    setLoading(true);
    squareSpaceAuth();
    //}
  };

  // Redirect to the squareSpaceAuth screen
  const squareSpaceAuth = () => {
    const endPointExtension = '';
    API.squareAuth(
      API.EndPoint.squareAuth + endPointExtension,
      API.Method.get,
      undefined,
      {},
      (response) => {
        if (response?.data) {
          location.href = response?.data?.authUrl;
        }
      }
    );
  };

  const handleSuccess = () => {
    let utm = getCookie(squareSpaceUtm);
    if (isLocalSeoSqsp && isFreePlan) {
      dispatch(setIsOpenChangePlan(true));
      navigator(paths?.billing);
    } else {
      navigator(
        isLocalSeoSqsp
          ? paths?.squareSpaceConnectUtmLocalSeo
          : isLocalSeo
          ? paths?.localChecker
          : utm
          ? paths?.squareSpaceConnectUtm
          : isFreePlan
          ? paths?.externalScans
          : paths.dashboard
      );
    }
    // navigator(paths.siteAudit);
  };

  const handleBack = () => {
    removeUtmCookie(isLoginOrSignup);
    setError('');
    setErrorSQAP(false);
    setLoading(false);
    const queryParams = new URLSearchParams(navigationLocation.search);
    const codeParam = queryParams.get('code');
    const squareSpaceUserEmail = localStorageMethod(
      'get',
      'squareSpaceAuthEmail'
    );
    const squareSpaceAuthName = localStorageMethod(
      'get',
      'squareSpaceAuthName'
    );

    setShowNameFiled(true);
  };

  return (
    <>
      <div
        className="flex justify-center md:grid grid-cols-2 h-full md:px-0"
        style={signupSxs?.sqspMainBoxSx}
      >
        {(isLocalSeo || isLocalSeoSession) && !utm ? (
          <LocalSeoToolPage />
        ) : (
          <SignUpToolPage />
        )}
        <div
          className="h-max bg-themeWhite py-5 h-full w-full flex items-center justify-center self-center md:p-[1rem] "
          style={{ ...signupSxs?.sqspThemeBox, flexDirection: 'column' }}
        >
          <Box sx={signupSxs?.backToSignUpBoxSx}>
            <img
              src="/static/images/icons/grey-arrow.svg"
              alt="SEOSpace"
              loading="lazy"
              className="cursor-pointer"
              onClick={() => {
                navigator(
                  isLocalSeoSqsp && !utm
                    ? paths?.signupLocalSeoExt
                    : (isLocalSeo || isLocalSeoSession) && !utm
                    ? paths?.signupLocalSeo
                    : paths?.signup
                );
              }}
            />
            <Typography
              sx={signupSxs?.backToSignUpSx}
              className="cursor-pointer"
              onClick={() => {
                navigator(
                  isLocalSeoSqsp && !utm
                    ? paths?.signupLocalSeoExt
                    : (isLocalSeo || isLocalSeoSession) && !utm
                    ? paths?.signupLocalSeo
                    : paths?.signup
                );
              }}
            >
              {signupTexts?.backToSignUp}
            </Typography>
          </Box>
          <Box sx={signupSxs?.sqspFormBoxSx}>
            <div
              className="flex justify-center align-middle"
              style={signupSxs?.sqspFormDivSx}
            >
              <div className="flex items-center justify-center relative h-full w-full ">
                <Box
                  sx={{
                    ...SquareSpaceConnectSx?.cardSx,
                    ...signupSxs?.sqspSubBoxSx
                  }}
                >
                  {/*<Box className="er w-[23.25rem]">*/}
                  <Box className="er">
                    <Box className="flex flex-col g justify-center items-center ">
                      <Box
                        component="form"
                        noValidate
                        autoComplete="off"
                        onSubmit={handleSubmit(onSubmit)}
                        className="w-full"
                      >
                        <Stack spacing={2} height="100%" alignItems="center">
                          <>
                            <Box sx={signupSxs?.titleBoxSx}>
                              <img
                                src={
                                  (isLocalSeo || isLocalSeoSession) && !utm
                                    ? localSeoLogo
                                    : '/static/images/logo/new-logo.svg'
                                }
                                alt="SEOSpace"
                                loading="lazy"
                                style={
                                  (isLocalSeo || isLocalSeoSession) && !utm
                                    ? LocalSeoIconSx
                                    : {}
                                }
                              />
                              <Typography sx={signupSxs?.titleSx}>
                                {signupTexts?.sqspTitle}
                              </Typography>
                            </Box>
                            <Typography sx={signupSxs?.sqSpDescSx}>
                              {SQSPDescription}
                            </Typography>
                            {showNameFiled ? (
                              <>
                                {/** User Name */}
                                <TextField
                                  //label="Name"
                                  placeholder="Name"
                                  size="small"
                                  type="text"
                                  autoComplete="name"
                                  fullWidth
                                  {...register(
                                    'name',
                                    signUpValidationErrorMessage.name
                                  )}
                                  helperText={
                                    errors.name ? errors.name.message : ''
                                  }
                                  error={!!errors.name}
                                  variant="outlined"
                                  sx={{
                                    ...loginSxs?.textFieldSx
                                  }}
                                  InputProps={{
                                    inputProps: { maxLength: 50 },
                                    sx: {
                                      ...loginSxs?.inputProps
                                    }
                                  }}
                                  InputLabelProps={{
                                    sx: {
                                      ...loginSxs?.inputLabelProps
                                    }
                                  }}
                                />
                                {/** User Email */}
                                <TextField
                                  variant="outlined"
                                  id="outlined-email-input"
                                  //label="Email"
                                  placeholder="Email"
                                  type="text"
                                  size="small"
                                  autoComplete="email"
                                  className="w-full"
                                  fullWidth
                                  {...preventSpaceInput(
                                    register(
                                      'email',
                                      signUpValidationErrorMessage.email
                                    )
                                  )}
                                  helperText={
                                    errors.email ? errors.email.message : ''
                                  }
                                  error={!!errors.email}
                                  sx={{
                                    ...loginSxs?.textFieldSx
                                  }}
                                  InputProps={{
                                    sx: {
                                      ...loginSxs?.inputProps
                                    }
                                  }}
                                  InputLabelProps={{
                                    sx: {
                                      ...loginSxs?.inputLabelProps
                                    }
                                  }}
                                />
                                {/** User Team size */}

                                {/** Other Field */}
                                {otherFields && (
                                  <TextField
                                    label="Please type here"
                                    size="small"
                                    type="text"
                                    InputProps={{
                                      disableUnderline: true,
                                      inputProps: { maxLength: 50 }
                                    }}
                                    fullWidth
                                    {...register('otherText')}
                                    variant="filled"
                                  />
                                )}
                              </>
                            ) : sqSpAuthLoader ? (
                              <>
                                <CircularProgress size={24} />
                              </>
                            ) : (
                              <>
                                {loading &&
                                  !isSuccessfullySubmitted &&
                                  !showNameFiled &&
                                  !errorSQAP &&
                                  !error && <CircularProgress size={36} />}
                                {isSuccessfullySubmitted && (
                                  <Box
                                    sx={
                                      SquareSpaceConnectSx?.accountConnectedBox
                                    }
                                  >
                                    <Typography
                                      sx={
                                        SquareSpaceConnectSx?.accountConnectedTypo
                                      }
                                    >
                                      {'Account Connected'}
                                    </Typography>
                                  </Box>
                                )}
                                {(errorSQAP || (error && !showNameFiled)) && (
                                  <Box
                                    sx={SquareSpaceConnectSx?.accountFailedBox}
                                  >
                                    <Typography
                                      sx={
                                        SquareSpaceConnectSx?.accountFailedTypo
                                      }
                                    >
                                      {'Connection Failed'}
                                    </Typography>
                                  </Box>
                                )}
                                {isSuccessfullySubmitted && (
                                  <Typography
                                    sx={SquareSpaceConnectSx?.successDescTypo}
                                  >
                                    {`Your Squarespace account has been successfully connected with ${
                                      isLocalSeo && isLocalSeoSession
                                        ? SEOSpaceLocal
                                        : 'SEOSpace'
                                    }. Thank you for linking your account!`}
                                  </Typography>
                                )}
                                {(errorSQAP || (error && !showNameFiled)) && (
                                  <Alert
                                    severity="error"
                                    className="mt-4"
                                    sx={signupSxs?.sqspAlertSx}
                                  >
                                    <Typography
                                      sx={signupSxs?.accessDeniedErrorSQAPSx}
                                    >
                                      {alreadyConnected
                                        ? accessDeniedErrorSQAP.alreadyConnectedError
                                        : isNotConnected &&
                                          (isLocalSeoSession ||
                                            isLocalSeoSqsp ||
                                            isLocalSeo) &&
                                          !utm
                                        ? accessDeniedErrorSQAP.noConnectionLocalSeoError
                                        : isNotConnected
                                        ? accessDeniedErrorSQAP.noConnectionError
                                        : accessDeniedErrorSQAP.first}
                                      <Link
                                        sx={
                                          signupSxs?.accessDeniedErrorSQAPLinkSx
                                        }
                                        onClick={() =>
                                          //Link not working
                                          //openNewTab(bugReportLink)
                                          handleInterComClick()
                                        }
                                      >
                                        {accessDeniedErrorSQAP.link}
                                      </Link>
                                    </Typography>
                                  </Alert>
                                )}
                                {isSuccessfullySubmitted && (
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    sx={SquareSpaceConnectSx?.successBtn}
                                    onClick={handleSuccess}
                                  >
                                    {'Got it!'}
                                  </Button>
                                )}
                                {(errorSQAP || (error && !showNameFiled)) && (
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    sx={SquareSpaceConnectSx?.successBtn}
                                    onClick={handleBack}
                                  >
                                    {'Sign Up'}
                                  </Button>
                                )}
                              </>
                            )}
                          </>

                          <Stack width="100%">
                            {isTermError && showNameFiled && (
                              <CommonAlert
                                severity="error"
                                sx={signupSxs?.termAlertSx}
                                message={signUpTexts?.termsError}
                              />
                            )}
                            {!isSuccessfullySubmitted && showNameFiled && (
                              <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                className="w-full"
                                disabled={loading}
                              >
                                {loading ? (
                                  <CircularProgress size={24} />
                                ) : (
                                  <>
                                    <Avatar
                                      sx={signupSxs?.avatarSx}
                                      src="/static/images/icons/squarespace.png"
                                    />
                                    {continueWithSquareSpace}
                                  </>
                                )}
                              </Button>
                            )}

                            {error && showNameFiled && (
                              <Alert severity="error" className="mt-4">
                                {alreadyConnected ? (
                                  <Typography
                                    sx={signupSxs?.accessDeniedErrorSQAPSx}
                                  >
                                    {
                                      accessDeniedErrorSQAP?.alreadyConnectedError
                                    }
                                    <Link
                                      sx={
                                        signupSxs?.accessDeniedErrorSQAPLinkSx
                                      }
                                      onClick={() =>
                                        //Link not working
                                        //openNewTab(bugReportLink)
                                        handleInterComClick()
                                      }
                                    >
                                      {accessDeniedErrorSQAP.link}
                                    </Link>
                                  </Typography>
                                ) : (
                                  <Typography
                                    sx={signupSxs?.createAccountLinkSx}
                                    dangerouslySetInnerHTML={{
                                      __html: createAccountLink(error)
                                    }}
                                  ></Typography>
                                )}
                              </Alert>
                            )}

                            <Box className="mt-3">
                              <Typography sx={signupSxs?.alreadyRegisterSx}>
                                {signupTexts?.alreadyAccount}
                                {'  '}{' '}
                                <span
                                  onClick={() => {
                                    navigator(paths?.signin);
                                  }}
                                  style={signupSxs?.loginSpanSx}
                                >
                                  {signupTexts?.login}
                                </span>
                              </Typography>
                            </Box>
                          </Stack>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {/*</div>*/}
              </div>
            </div>
            <SignupFooter />
          </Box>
        </div>
      </div>
      {openIntercom && <Intercom appID={INTERCOM_APP_ID} />}
    </>
  );
};

export default SquareSpaceAuthSignUp;
